import React, { useEffect, useState } from 'react';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import './styles/Modal.css'; // We'll consolidate styles in this file

/**
 * Reusable Modal component that can be used for success messages, alerts, or other content
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls modal visibility
 * @param {function} props.onClose - Function called when modal is closed
 * @param {string} props.variant - Modal variant ('success' or 'alert')
 * @param {string} props.title - Modal title/header
 * @param {string} props.body - Modal body content (can include HTML for alert variant)
 * @param {string} props.actionText - Text for action button (optional)
 * @param {string} props.actionUrl - URL for action button (optional)
 * @param {string} props.imageSrc - Image source URL (optional, for success variant)
 * @param {string} props.imageAlt - Image alt text (optional)
 * @param {string} props.size - Modal size ('sm', 'lg', 'xl')
 */
export const Modal = ({
  show = false,
  onClose = () => {},
  variant = 'alert',
  title,
  body,
  actionText,
  actionUrl,
  imageSrc,
  imageAlt = '',
  size = 'lg',
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  // Determine modal classes based on variant
  const getModalContentClass = () => {
    return variant === 'success'
      ? 'modal-custom__content modal-custom__content--success'
      : 'modal-custom__content';
  };

  return (
    <BootstrapModal
      show={isVisible}
      onHide={handleClose}
      centered
      size={size}
      contentClassName={getModalContentClass()}>
      {variant === 'alert' ? (
        // Alert variant
        <>
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title className="modal-custom__title">{title}</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <div className="modal-custom__body">
              {typeof body === 'string' ? parse(body) : body}
            </div>
          </BootstrapModal.Body>
          <BootstrapModal.Footer>
            <Button
              variant="secondary"
              className="modal-custom__close-button"
              onClick={handleClose}>
              Close
            </Button>
          </BootstrapModal.Footer>
        </>
      ) : (
        // Success variant
        <BootstrapModal.Body>
          <div className="modal-custom__body">
            <div className="modal-custom__header">
              <h2>{title}</h2>
            </div>

            {imageSrc && (
              <div className="modal-custom__image-container">
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="modal-custom__rounded-image"
                  width={'100%'}
                  loading="lazy"
                />
              </div>
            )}

            {body && (
              <div className="modal-custom__subheader">
                {typeof body === 'string' ? parse(body) : body}
              </div>
            )}

            {actionText && (
              <div className="modal-custom__action-container">
                <a
                  href={actionUrl || '#'}
                  className="modal-custom__action-button"
                  onClick={!actionUrl ? handleClose : undefined}
                  autoFocus>
                  {actionText}
                </a>
              </div>
            )}
          </div>
        </BootstrapModal.Body>
      )}
    </BootstrapModal>
  );
};

Modal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['success', 'alert']),
  title: PropTypes.string.isRequired,
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actionText: PropTypes.string,
  actionUrl: PropTypes.string,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  size: PropTypes.string,
};

export default Modal;
